import { DynamicWidget, useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { AlertTriangle, Menu } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { useProjectPlan, useProjectStats } from 'src/modules/overview/hooks';
import { useProjectsContext } from 'src/modules/projects/context';

import ThemeSwitcher from '@/components/ThemeSwitcher';
import { WrongNetwork } from '@/components/WrongNetwork';
import { Badge, Button, Text, Tooltip } from '@/design-system/components/ui';
import { useMediaQuery } from '@/design-system/hooks/useMediaQuery';

const TOP_NAV_HEIGHT = 64;
const SIDE_NAV_WIDTH = 280;

interface TopNavProps {
  onMobileNavOpen?: () => void;
}

export const TopNav: FC<TopNavProps> = (props) => {
  const { onMobileNavOpen, ...other } = props;
  const lgUp = useMediaQuery('(min-width: 1200px)');

  const { project } = useProjectsContext();
  const { network: connectedNetwork } = useDynamicContext();
  const { data: projectStats } = useProjectStats();
  const { data: planDetails } = useProjectPlan();

  const showTestModeWarning = useMemo(() => {
    const hasPointRewards = projectStats?.rewards?.some((reward) => reward.currency_name === 'POINT');
    const hasPointPayoutTerms = project?.conversions?.some((conversion) =>
      conversion.payoutTerms?.some((term) => term.type === 'point'),
    );
    const isTestMode = planDetails?.credits === 500;
    return (hasPointRewards || hasPointPayoutTerms) && isTestMode;
  }, [project?.conversions, projectStats?.rewards]);

  const shouldChangeNetworkBeforeProceeding = useMemo(
    () => String(connectedNetwork) !== String(project?.contractChainId),
    [connectedNetwork, project?.contractChainId],
  );

  return (
    <>
      <header
        className="tw:bg-background"
        style={{
          backdropFilter: 'blur(6px)',
          position: 'sticky',
          left: lgUp ? `${SIDE_NAV_WIDTH}px` : undefined,
          top: 0,
          width: lgUp ? `calc(100% - ${SIDE_NAV_WIDTH}px)` : undefined,
          zIndex: 1100,
        }}
        {...other}
      >
        <div
          className="tw:flex tw:flex-row tw:gap-4 tw:items-center tw:justify-between tw:px-4 tw:overflow-x-clip"
          style={{ minHeight: TOP_NAV_HEIGHT }}
        >
          {showTestModeWarning && (
            <Tooltip
              content={
                'Track up to 500 events while setting up your program. Contact your Fuul engagement manager when ready to go live with full tracking.'
              }
            >
              <Badge variant="warning">
                <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
                  <AlertTriangle size={16} />
                  <Text variant="body-sm">Test Mode Active</Text>
                </div>
              </Badge>
            </Tooltip>
          )}
          <div className="tw:flex tw:flex-row tw:gap-4 tw:items-center">
            {!lgUp && (
              <Button variant="ghost" size="icon" onClick={onMobileNavOpen}>
                <Menu size={20} />
              </Button>
            )}
          </div>
          <div className="tw:flex tw:flex-row tw:gap-4 tw:items-center">
            <ThemeSwitcher />
            {project?.contractChainId && shouldChangeNetworkBeforeProceeding && <WrongNetwork />}
            <DynamicWidget
              innerButtonComponent={
                <Text variant="body-md" weight="semibold">
                  Sign in or sign up
                </Text>
              }
            />
          </div>
        </div>
      </header>
    </>
  );
};
